import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  BadgeInfo, Check, Circle, Cloud, Highlighter, Loader2, Maximize, MousePointer2, MoveUpRight, Palette, Pencil, PenLine, Redo2, Trash2, Type, Undo2, X, ZoomIn, ZoomOut,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";

/* ----------------------------------------------------------------------------
 * Photo markup (Phase 90) — circle / arrow / pen / highlighter / revision cloud
 * with callout text / text labels / auto-fitted info stamp, flattened to a JPEG.
 * All geometry lives in IMAGE PIXEL coordinates (the SVG viewBox is the photo),
 * so the export matches the original resolution regardless of screen size.
 * Text uses the Arial/Helvetica stack: SVG-as-image can't see web fonts, and the
 * same stack is used to measure, so wrapped text fits in both preview and export.
 * -------------------------------------------------------------------------- */

const FONT = "Arial, Helvetica, sans-serif";
const INK = "#1F2A3A";
const PALE = "#B9C7D8";
const STEEL = "#2B6CB0";

export const MARKUP_SOLIDS = [
  { id: "red", hex: "#E5322D" }, { id: "orange", hex: "#F97316" }, { id: "yellow", hex: "#FACC15" }, { id: "green", hex: "#16A34A" },
  { id: "teal", hex: "#0F8A8A" }, { id: "blue", hex: "#2B6CB0" }, { id: "purple", hex: "#7C3AED" }, { id: "pink", hex: "#DB2777" },
  { id: "white", hex: "#FFFFFF" }, { id: "black", hex: "#111827" },
];
export const MARKUP_HIGHLIGHTS = [
  { id: "yellow", hex: "#FFF200" }, { id: "lime", hex: "#A3FF12" }, { id: "pink", hex: "#FF6EC7" }, { id: "cyan", hex: "#38E8FF" }, { id: "orange", hex: "#FFA62B" }, { id: "lavender", hex: "#C084FC" },
];
const SIZES = [{ id: "s", k: 0.6, label: "Fine" }, { id: "m", k: 1, label: "Medium" }, { id: "l", k: 1.7, label: "Bold" }];
const TOOLS = [
  { id: "select", icon: MousePointer2, label: "Select / move" },
  { id: "pen", icon: Pencil, label: "Pen" },
  { id: "highlight", icon: Highlighter, label: "Highlighter" },
  { id: "ellipse", icon: Circle, label: "Circle" },
  { id: "arrow", icon: MoveUpRight, label: "Arrow" },
  { id: "cloud", icon: Cloud, label: "Revision cloud + callout" },
  { id: "text", icon: Type, label: "Text label" },
];
const STAMP_ROWS = [["SITE", "site"], ["UNIT", "unit"], ["AREA", "area"], ["DEFECT", "description"], ["ACTION", "trade"]];

/* ---------- text helpers (shared by preview + export) ---------- */
let _measureCtx = null;
const measure = (text, px, weight = 400) => {
  if (!_measureCtx) _measureCtx = document.createElement("canvas").getContext("2d");
  _measureCtx.font = `${weight} ${px}px ${FONT}`;
  return _measureCtx.measureText(text).width;
};
const wrapText = (text, px, maxW, weight = 400) => {
  const out = [];
  const paragraphs = String(text || "").replace(/\r/g, "").split("\n");
  paragraphs.forEach((para) => {
    const words = para.split(/\s+/).filter(Boolean);
    if (!words.length) return;
    let line = "";
    words.forEach((w) => {
      let word = w;
      // hard-break words wider than the box
      while (measure(word, px, weight) > maxW && word.length > 1) {
        let cut = word.length - 1;
        while (cut > 1 && measure(word.slice(0, cut), px, weight) > maxW) cut -= 1;
        if (line) { out.push(line); line = ""; }
        out.push(word.slice(0, cut));
        word = word.slice(cut);
      }
      const candidate = line ? `${line} ${word}` : word;
      if (measure(candidate, px, weight) <= maxW) line = candidate;
      else { if (line) out.push(line); line = word; }
    });
    if (line) out.push(line);
  });
  return out.length ? out : [""];
};
const ellipsize = (line, px, maxW, weight = 400) => {
  let s = line;
  while (s.length > 1 && measure(`${s}…`, px, weight) > maxW) s = s.slice(0, -1);
  return `${s.trimEnd()}…`;
};
const luminance = (hex) => {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || "");
  if (!m) return 0.5;
  const [r, g, b] = [m[1], m[2], m[3]].map((h) => parseInt(h, 16) / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};
const haloFor = (hex) => (luminance(hex) > 0.55 ? INK : "#FFFFFF");
const hslToHex = (h, s, l) => {
  const a = (s * Math.min(l, 1 - l));
  const f = (n) => { const k = (n + h / 30) % 12; const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1)); return Math.round(255 * c).toString(16).padStart(2, "0"); };
  return `#${f(0)}${f(8)}${f(4)}`;
};

/* ---------- geometry helpers ---------- */
const dist = (x1, y1, x2, y2) => Math.hypot(x2 - x1, y2 - y1);
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const smoothPath = (pts) => {
  if (!pts.length) return "";
  if (pts.length < 3) return `M ${pts.map((p) => `${p[0]} ${p[1]}`).join(" L ")}`;
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 1; i < pts.length - 1; i += 1) {
    const mx = (pts[i][0] + pts[i + 1][0]) / 2;
    const my = (pts[i][1] + pts[i + 1][1]) / 2;
    d += ` Q ${pts[i][0]} ${pts[i][1]} ${mx} ${my}`;
  }
  const last = pts[pts.length - 1];
  return `${d} L ${last[0]} ${last[1]}`;
};
const normRect = (el) => ({ xa: Math.min(el.x1, el.x2), ya: Math.min(el.y1, el.y2), xb: Math.max(el.x1, el.x2), yb: Math.max(el.y1, el.y2) });
/** Revision cloud: scalloped arcs walked clockwise around the rectangle, bulging outward. */
const cloudPath = (el, r) => {
  const { xa, ya, xb, yb } = normRect(el);
  const w = xb - xa;
  const h = yb - ya;
  const rr = Math.max(2, Math.min(r, w / 2.2, h / 2.2));
  const nx = Math.max(1, Math.round(w / (1.7 * rr)));
  const ny = Math.max(1, Math.round(h / (1.7 * rr)));
  const sx = w / nx;
  const sy = h / ny;
  const rx = sx * 0.62;
  const ry = sy * 0.62;
  let d = `M ${xa} ${ya}`;
  for (let i = 1; i <= nx; i += 1) d += ` A ${rx} ${rx} 0 0 1 ${xa + i * sx} ${ya}`;
  for (let i = 1; i <= ny; i += 1) d += ` A ${ry} ${ry} 0 0 1 ${xb} ${ya + i * sy}`;
  for (let i = 1; i <= nx; i += 1) d += ` A ${rx} ${rx} 0 0 1 ${xb - i * sx} ${yb}`;
  for (let i = 1; i <= ny; i += 1) d += ` A ${ry} ${ry} 0 0 1 ${xa} ${yb - i * sy}`;
  return `${d} Z`;
};
const arrowHeadPts = (x1, y1, x2, y2, size) => {
  const a = Math.atan2(y2 - y1, x2 - x1);
  const p = (ang) => `${x2 - size * Math.cos(a + ang)},${y2 - size * Math.sin(a + ang)}`;
  return `${x2},${y2} ${p(0.42)} ${p(-0.42)}`;
};
/** Where a ray from (cx,cy) in direction (dx,dy) leaves the rectangle. */
const rayRectExit = (cx, cy, dx, dy, { xa, ya, xb, yb }) => {
  let t = Infinity;
  if (dx > 0) t = Math.min(t, (xb - cx) / dx);
  if (dx < 0) t = Math.min(t, (xa - cx) / dx);
  if (dy > 0) t = Math.min(t, (yb - cy) / dy);
  if (dy < 0) t = Math.min(t, (ya - cy) / dy);
  if (!Number.isFinite(t)) t = 0;
  return [cx + dx * t, cy + dy * t];
};

/** Callout label box for a cloud: wrapped lines + box size (image px). */
const calloutLayout = (el, u, W) => {
  if (!el.text) return null;
  const fs = (el.fontScale || 1) * 26 * u;
  const pad = fs * 0.55;
  const maxTextW = Math.max(fs * 4, W * 0.42);
  const lines = wrapText(el.text, fs, maxTextW, 700).slice(0, 6);
  const textW = Math.max(...lines.map((l) => measure(l, fs, 700)));
  const lineH = fs * 1.22;
  return { fs, pad, lines, w: textW + pad * 2, h: lines.length * lineH + pad * 2, lineH };
};
const defaultLabelPos = (el, lay, W, H, u) => {
  const { xa, ya, xb, yb } = normRect(el);
  const gap = 34 * u;
  let lx = clamp(xb - lay.w * 0.6, W * 0.02, W - lay.w - W * 0.02);
  let ly = ya - gap - lay.h;
  if (ly < H * 0.02) ly = yb + gap;
  if (ly + lay.h > H * 0.98) ly = clamp((ya + yb) / 2 - lay.h / 2, H * 0.02, H - lay.h - H * 0.02);
  if (ly + lay.h > ya && ly < yb) lx = xb + gap <= W - lay.w ? xb + gap : Math.max(W * 0.02, xa - gap - lay.w);
  return { lx, ly };
};

/** Info stamp layout: two-column (kicker · value) block that shrinks/wraps until it fits ≤ 42% of the photo height. */
const layoutStamp = (W, H, stamp) => {
  if (!stamp?.enabled) return null;
  const rows = STAMP_ROWS.map(([label, key]) => [label, String(stamp[key] || "").trim()]).filter(([, v]) => v);
  if (!rows.length) return null;
  const margin = Math.round(W * 0.025);
  const boxW = W - margin * 2;
  const maxH = H * 0.42;
  let fs = clamp(W / 38, 11, 34);
  for (let guard = 0; guard < 40; guard += 1) {
    const pad = fs * 0.75;
    const accentW = fs * 0.35;
    const kickerPx = fs * 0.68;
    const labelW = Math.max(...rows.map(([l]) => measure(l, kickerPx, 700))) + fs * 1.1;
    const textW = boxW - pad * 2 - accentW - labelW;
    const lineH = fs * 1.28;
    const rowGap = fs * 0.5;
    const laid = rows.map(([label, value]) => ({ label, lines: wrapText(value, fs, textW, label === "SITE" ? 700 : 400), weight: label === "SITE" ? 700 : 400 }));
    const height = () => pad * 2 + laid.reduce((s, r) => s + r.lines.length * lineH, 0) + (laid.length - 1) * rowGap;
    if (height() <= maxH || fs <= 9.5) {
      // last resort at minimum size: trim the longest rows with an ellipsis
      let safety = 0;
      while (height() > maxH && safety < 30) {
        const longest = laid.reduce((a, b) => (b.lines.length > a.lines.length ? b : a));
        if (longest.lines.length <= 1) break;
        longest.lines = longest.lines.slice(0, -1);
        longest.lines[longest.lines.length - 1] = ellipsize(longest.lines[longest.lines.length - 1], fs, textW, longest.weight);
        safety += 1;
      }
      const h = height();
      const y = stamp.position === "top" ? margin : H - margin - h;
      return { x: margin, y, w: boxW, h, fs, pad, accentW, kickerPx, labelW, lineH, rowGap, laid };
    }
    fs *= 0.92;
  }
  return null;
};

/* ---------- zoom / pan (Phase 91C) ----------
 * view = { z, x, y }: z is the zoom factor on top of the fit-to-box scale, x/y are the CSS px
 * translation of the (centred) photo. Geometry is always kept in image px, so zoom is purely a
 * CSS transform; pointer → image mapping goes through getBoundingClientRect so it stays exact. */
const MIN_ZOOM = 1;
const MAX_ZOOM = 6;
const ZOOM_STEP = 1.5;
const FIT_VIEW = { z: 1, x: 0, y: 0 };
/** Keep the photo covering the box: pan is only allowed along axes where the photo overflows. */
const clampView = (z, x, y, g) => {
  const zz = clamp(z, MIN_ZOOM, MAX_ZOOM);
  const maxX = Math.max(0, (g.dispW * zz - g.boxW) / 2);
  const maxY = Math.max(0, (g.dispH * zz - g.boxH) / 2);
  return { z: zz, x: clamp(x, -maxX, maxX), y: clamp(y, -maxY, maxY) };
};
/** Zoom to nextZ keeping the photo point under (cx, cy) (box-centre-relative px) fixed on screen. */
const zoomAbout = (v, nextZ, cx, cy, g) => {
  const z = clamp(nextZ, MIN_ZOOM, MAX_ZOOM);
  const k = z / v.z;
  return clampView(z, cx - (cx - v.x) * k, cy - (cy - v.y) * k, g);
};

const approxBytes = (dataUrl) => Math.round(((dataUrl.length - (dataUrl.indexOf(",") + 1)) * 3) / 4);
const loadImage = (url) => new Promise((resolve, reject) => { const img = new Image(); img.onload = () => resolve(img); img.onerror = () => reject(new Error("Couldn't load the photo")); img.src = url; });

let _idSeq = 1;
const nextId = () => `mk-${Date.now().toString(36)}-${_idSeq++}`;
/** setPointerCapture throws if the pointer is already gone (fast taps, synthetic events) — never let that break a stroke. */
const capturePointer = (el, pointerId) => { try { el?.setPointerCapture?.(pointerId); } catch { /* ignore */ } };

const EMPTY_STAMP = { enabled: false, position: "bottom", site: "", unit: "", area: "", description: "", trade: "", touched: {} };

/**
 * PhotoMarkupDialog
 * props: open, onOpenChange, src (data/blob URL) | authPath (API path fetched with auth), title, subtitle,
 *        stampDefaults {site, unit, description, trade}, initial {elements, stamp} (re-open a previous session),
 *        onSave({ dataUrl, bytes, elements, stamp })
 */
export const PhotoMarkupDialog = ({ open, onOpenChange, src, authPath, title = "Mark up photo", subtitle, stampDefaults, initial, onSave }) => {
  const svgRef = useRef(null);
  const boxRef = useRef(null);
  const baseImgRef = useRef(null);
  const dragRef = useRef(null);
  const [imgUrl, setImgUrl] = useState("");
  const [dims, setDims] = useState(null); // {W,H}
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [loadError, setLoadError] = useState("");

  const [tool, setTool] = useState("ellipse");
  const [color, setColor] = useState(MARKUP_SOLIDS[0].hex);
  const [hiColor, setHiColor] = useState(MARKUP_HIGHLIGHTS[0].hex);
  const [size, setSize] = useState("m");
  const [hue, setHue] = useState(0);
  const [elements, setElements] = useState([]);
  const [draft, setDraft] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [textEdit, setTextEdit] = useState(null); // {mode:'text'|'cloud', x, y, id?, value}
  const [stamp, setStamp] = useState(EMPTY_STAMP);
  const [stampOpen, setStampOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState(FIT_VIEW); // zoom + pan
  const pointersRef = useRef(new Map()); // pointerId -> client {x,y} (for pinch)
  const gestureRef = useRef(null); // {type:'pan'|'pinch'|'cooldown', ...}
  const geomRef = useRef({ dispW: 0, dispH: 0, boxW: 0, boxH: 0 });

  /* ---------- load photo ---------- */
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    let objectUrl = "";
    setDims(null); setLoadError(""); setImgUrl(""); setView(FIT_VIEW);
    pointersRef.current.clear(); gestureRef.current = null;
    const run = async () => {
      try {
        let url = src || "";
        if (!url && authPath) {
          const res = await api.get(authPath, { responseType: "blob" });
          objectUrl = URL.createObjectURL(res.data);
          url = objectUrl;
        }
        if (!url) throw new Error("No photo to mark up");
        const img = await loadImage(url);
        if (cancelled) return;
        baseImgRef.current = img;
        setImgUrl(url);
        setDims({ W: img.naturalWidth || img.width, H: img.naturalHeight || img.height });
      } catch (e) {
        if (!cancelled) setLoadError(e.message || "Couldn't load the photo");
      }
    };
    run();
    return () => { cancelled = true; if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 0); };
  }, [open, src, authPath]);

  /* ---------- reset editor state on open ---------- */
  useEffect(() => {
    if (!open) return;
    const prev = initial?.stamp;
    const touched = prev?.touched || {};
    const d = stampDefaults || {};
    setElements(initial?.elements || []);
    setStamp({
      enabled: prev ? Boolean(prev.enabled) : false,
      position: prev?.position || "bottom",
      site: touched.site ? prev.site : d.site || "",
      unit: touched.unit ? prev.unit : d.unit || "",
      area: touched.area ? prev.area : d.area || "",
      description: touched.description ? prev.description : d.description || "",
      trade: touched.trade ? prev.trade : d.trade || "",
      touched,
    });
    setHistory([]); setRedoStack([]); setSelectedId(null); setDraft(null); setTextEdit(null); setStampOpen(false); setSaving(false);
    setTool("ellipse");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ---------- fit the photo into the available box ---------- */
  useLayoutEffect(() => {
    if (!open) return undefined;
    const node = boxRef.current;
    if (!node) return undefined;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) setBox({ w: r.width, h: r.height });
    });
    ro.observe(node);
    setBox({ w: node.clientWidth, h: node.clientHeight });
    return () => ro.disconnect();
  }, [open, dims]);

  const W = dims?.W || 0;
  const H = dims?.H || 0;
  const u = Math.max(W, H) / 1000 || 1; // size unit relative to the photo
  const fit = dims && box.w && box.h ? Math.min(box.w / W, box.h / H) : 0;
  const dispW = Math.floor(W * fit);
  const dispH = Math.floor(H * fit);
  const sizeK = SIZES.find((s) => s.id === size)?.k || 1;
  const strokeW = 5 * u * sizeK;
  const activeColor = tool === "highlight" ? hiColor : color;
  geomRef.current = { dispW, dispH, boxW: box.w, boxH: box.h };

  /* ---------- zoom / pan ---------- */
  const boxPoint = (clientX, clientY) => {
    const r = boxRef.current.getBoundingClientRect();
    return { x: clientX - r.left - r.width / 2, y: clientY - r.top - r.height / 2 };
  };
  const zoomBy = (factor) => setView((v) => zoomAbout(v, v.z * factor, 0, 0, geomRef.current));
  const resetView = () => setView(FIT_VIEW);
  // re-clamp when the box or photo size changes (orientation change, dialog resize)
  useEffect(() => { setView((v) => clampView(v.z, v.x, v.y, geomRef.current)); }, [box.w, box.h, dispW, dispH]);
  // mouse-wheel / trackpad zoom about the cursor (native listener: React's onWheel is passive)
  useEffect(() => {
    if (!open) return undefined;
    const node = boxRef.current;
    if (!node) return undefined;
    const onWheel = (e) => {
      if (!geomRef.current.dispW) return;
      e.preventDefault();
      const r = node.getBoundingClientRect();
      const cx = e.clientX - r.left - r.width / 2;
      const cy = e.clientY - r.top - r.height / 2;
      const factor = Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.0022));
      setView((v) => zoomAbout(v, v.z * factor, cx, cy, geomRef.current));
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    return () => node.removeEventListener("wheel", onWheel);
  }, [open, dims]);

  const commit = useCallback((next) => {
    setHistory((h) => [...h.slice(-59), elements]);
    setRedoStack([]);
    setElements(next);
  }, [elements]);
  const undo = () => { setHistory((h) => { if (!h.length) return h; setRedoStack((r) => [...r, elements]); setElements(h[h.length - 1]); return h.slice(0, -1); }); setSelectedId(null); };
  const redo = () => { setRedoStack((r) => { if (!r.length) return r; setHistory((h) => [...h, elements]); setElements(r[r.length - 1]); return r.slice(0, -1); }); };

  /** Pointer → image px. Uses the SVG's on-screen rect so CSS zoom/pan transforms are accounted for. */
  const svgPoint = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    const x = r.width ? ((e.clientX - r.left) / r.width) * W : 0;
    const y = r.height ? ((e.clientY - r.top) / r.height) * H : 0;
    return { x: clamp(x, 0, W), y: clamp(y, 0, H) };
  };
  const isUiTarget = (e) => Boolean(e.target?.closest?.("[data-markup-ui]"));
  const startPan = (e) => { gestureRef.current = { type: "pan", startX: e.clientX, startY: e.clientY, startView: view }; };

  /* ---------- pointer handlers ----------
   * All move/up handling lives on the canvas box (which captures every pointer) so a stroke that
   * starts on the photo keeps tracking when the finger leaves it, and so a second finger can turn
   * any gesture into a pinch-zoom. */
  const onBoxPointerDownCapture = (e) => {
    if (!dims || isUiTarget(e)) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    capturePointer(boxRef.current, e.pointerId);
    if (pointersRef.current.size === 2) {
      // second finger: abandon the in-progress stroke / drag and pinch-zoom instead
      const [p1, p2] = [...pointersRef.current.values()];
      const d = dragRef.current;
      if (d?.id) setElements(d.before);
      dragRef.current = null;
      setDraft(null);
      gestureRef.current = { type: "pinch", startDist: Math.max(1, dist(p1.x, p1.y, p2.x, p2.y)), startMid: boxPoint((p1.x + p2.x) / 2, (p1.y + p2.y) / 2), startView: view };
    }
  };
  const onBoxPointerDown = (e) => {
    // a press on the margin around the photo (outside the SVG) pans when zoomed
    if (!dims || textEdit || gestureRef.current || isUiTarget(e)) return;
    if (svgRef.current?.contains(e.target)) return;
    startPan(e);
  };
  const onPointerDown = (e) => {
    if (!dims || textEdit || gestureRef.current) return;
    e.preventDefault();
    const { x, y } = svgPoint(e);
    if (tool === "select") { setSelectedId(null); startPan(e); return; }
    if (tool === "text") { dragRef.current = { pendingText: { x, y } }; return; }
    const base = { id: nextId(), color: activeColor, width: strokeW };
    if (tool === "pen" || tool === "highlight") setDraft({ ...base, type: tool, points: [[x, y]], width: tool === "highlight" ? 26 * u * sizeK : strokeW });
    else setDraft({ ...base, type: tool, x1: x, y1: y, x2: x, y2: y });
  };
  const onPointerMove = (e) => {
    if (!dims) return;
    const ptrs = pointersRef.current;
    if (ptrs.has(e.pointerId)) ptrs.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const g = gestureRef.current;
    if (g?.type === "pinch") {
      if (ptrs.size < 2) return;
      const [p1, p2] = [...ptrs.values()];
      const mid = boxPoint((p1.x + p2.x) / 2, (p1.y + p2.y) / 2);
      const z = clamp(g.startView.z * (dist(p1.x, p1.y, p2.x, p2.y) / g.startDist), MIN_ZOOM, MAX_ZOOM);
      const k = z / g.startView.z;
      setView(clampView(z, mid.x - (g.startMid.x - g.startView.x) * k, mid.y - (g.startMid.y - g.startView.y) * k, geomRef.current));
      return;
    }
    if (g?.type === "pan") {
      setView(clampView(g.startView.z, g.startView.x + (e.clientX - g.startX), g.startView.y + (e.clientY - g.startY), geomRef.current));
      return;
    }
    if (g || !svgRef.current) return;
    const { x, y } = svgPoint(e);
    const d = dragRef.current;
    if (d?.id) {
      const { id, orig, start, handle } = d;
      const dx = x - start.x;
      const dy = y - start.y;
      setElements((els) => els.map((el) => {
        if (el.id !== id) return el;
        if (handle === "p1") return { ...el, x1: x, y1: y };
        if (handle === "p2") return { ...el, x2: x, y2: y };
        if (handle === "size") return { ...el, x2: Math.max(orig.x1 + 8 * u, x), y2: Math.max(orig.y1 + 8 * u, y) };
        if (handle === "label") return { ...el, lx: orig.lx + dx, ly: orig.ly + dy };
        if (el.points) return { ...el, points: orig.points.map(([px, py]) => [px + dx, py + dy]) };
        if (el.type === "text") return { ...el, x: orig.x + dx, y: orig.y + dy };
        return { ...el, x1: orig.x1 + dx, y1: orig.y1 + dy, x2: orig.x2 + dx, y2: orig.y2 + dy, lx: orig.lx == null ? orig.lx : orig.lx + dx, ly: orig.ly == null ? orig.ly : orig.ly + dy };
      }));
      return;
    }
    if (!draft) return;
    if (draft.points) setDraft((dr) => ({ ...dr, points: [...dr.points, [x, y]] }));
    else setDraft((dr) => ({ ...dr, x2: x, y2: y }));
  };
  const onPointerUp = (e) => {
    const ptrs = pointersRef.current;
    if (e?.pointerId != null) ptrs.delete(e.pointerId);
    const g = gestureRef.current;
    if (g) {
      // after a pinch, swallow the remaining finger until everything is lifted so it can't draw a stray mark
      if (g.type === "pinch") gestureRef.current = ptrs.size ? { type: "cooldown" } : null;
      else if (g.type === "cooldown") { if (!ptrs.size) gestureRef.current = null; }
      else gestureRef.current = null;
      return;
    }
    const d = dragRef.current;
    if (d?.pendingText) {
      dragRef.current = null;
      setTextEdit({ mode: "text", x: d.pendingText.x, y: d.pendingText.y, value: "" });
      return;
    }
    if (d?.id) {
      dragRef.current = null;
      setHistory((h) => [...h.slice(-59), d.before]);
      setRedoStack([]);
      return;
    }
    if (!draft) return;
    const tooSmall = draft.points ? draft.points.length < 2 : dist(draft.x1, draft.y1, draft.x2, draft.y2) < 6 * u;
    if (tooSmall) { setDraft(null); return; }
    let el = draft;
    if (el.type === "ellipse" || el.type === "cloud") {
      const { xa, ya, xb, yb } = normRect(el);
      el = { ...el, x1: xa, y1: ya, x2: xb, y2: yb };
    }
    commit([...elements, el]);
    setDraft(null);
    if (el.type === "cloud") {
      setSelectedId(el.id);
      setTextEdit({ mode: "cloud", id: el.id, value: "" });
    }
  };
  const startDrag = (e, el, handle = null) => {
    if (tool !== "select" || gestureRef.current) return;
    e.stopPropagation();
    e.preventDefault();
    capturePointer(boxRef.current, e.pointerId);
    setSelectedId(el.id);
    dragRef.current = { id: el.id, handle, start: svgPoint(e), orig: JSON.parse(JSON.stringify(el)), before: elements };
  };

  /* ---------- element actions ---------- */
  const selected = useMemo(() => elements.find((el) => el.id === selectedId) || null, [elements, selectedId]);
  const deleteSelected = useCallback(() => {
    if (!selectedId) return;
    commit(elements.filter((el) => el.id !== selectedId));
    setSelectedId(null);
  }, [selectedId, elements, commit]);
  const pickColor = (hex, forHighlight = false) => {
    if (forHighlight) setHiColor(hex); else setColor(hex);
    if (selected) commit(elements.map((el) => (el.id === selectedId ? { ...el, color: hex } : el)));
  };
  const pickSize = (id) => {
    setSize(id);
    if (selected) {
      const k = SIZES.find((s) => s.id === id)?.k || 1;
      commit(elements.map((el) => (el.id === selectedId ? { ...el, width: el.type === "highlight" ? 26 * u * k : 5 * u * k, fontScale: k } : el)));
    }
  };
  const editSelectedText = () => {
    if (!selected) return;
    if (selected.type === "text") setTextEdit({ mode: "text", id: selected.id, x: selected.x, y: selected.y, value: selected.text || "" });
    if (selected.type === "cloud") setTextEdit({ mode: "cloud", id: selected.id, value: selected.text || "" });
  };
  const commitText = () => {
    if (!textEdit) return;
    const value = textEdit.value.trim();
    if (textEdit.mode === "text") {
      if (textEdit.id) commit(value ? elements.map((el) => (el.id === textEdit.id ? { ...el, text: value } : el)) : elements.filter((el) => el.id !== textEdit.id));
      else if (value) {
        const el = { id: nextId(), type: "text", x: textEdit.x, y: textEdit.y, text: value, color, fontScale: sizeK };
        commit([...elements, el]);
        setSelectedId(el.id);
      }
    } else if (textEdit.mode === "cloud") {
      commit(elements.map((el) => {
        if (el.id !== textEdit.id) return el;
        if (!value) return { ...el, text: "", lx: undefined, ly: undefined };
        const next = { ...el, text: value };
        if (next.lx == null) {
          const lay = calloutLayout(next, u, W);
          Object.assign(next, defaultLabelPos(next, lay, W, H, u));
        }
        return next;
      }));
    }
    setTextEdit(null);
  };

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.target && ["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); if (e.shiftKey) redo(); else undo(); return; }
      if ((e.key === "Delete" || e.key === "Backspace") && selectedId && !textEdit) { e.preventDefault(); deleteSelected(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }); // intentionally re-bound each render so closures stay fresh

  /* ---------- stamp ---------- */
  const setStampField = (key, value) => setStamp((s) => ({ ...s, [key]: value, touched: { ...s.touched, [key]: true } }));
  const stampLayout = useMemo(() => (dims ? layoutStamp(W, H, stamp) : null), [dims, W, H, stamp]);

  /* ---------- export ---------- */
  const flatten = async () => {
    const svg = svgRef.current;
    const base = baseImgRef.current;
    if (!svg || !base) throw new Error("Photo not ready");
    const clone = svg.cloneNode(true);
    clone.querySelectorAll("[data-ui]").forEach((n) => n.remove());
    clone.setAttribute("width", W);
    clone.setAttribute("height", H);
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    const xml = new XMLSerializer().serializeToString(clone);
    const overlay = await loadImage(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`);
    const canvas = document.createElement("canvas");
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, W, H);
    ctx.drawImage(base, 0, 0, W, H);
    ctx.drawImage(overlay, 0, 0, W, H);
    return canvas.toDataURL("image/jpeg", 0.88);
  };
  const save = async () => {
    if (!dims) return;
    if (!elements.length && !stampLayout) { toast.info("Draw something or add the info stamp first"); return; }
    setSaving(true);
    try {
      const dataUrl = await flatten();
      onSave?.({ dataUrl, bytes: approxBytes(dataUrl), elements, stamp });
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message || "Couldn't apply the markup");
    } finally {
      setSaving(false);
    }
  };

  /* ---------- render helpers ---------- */
  const handleR = (13 * u) / Math.sqrt(view.z); // handles stay a sensible finger size when zoomed in
  const renderHandles = (el) => {
    if (el.id !== selectedId || tool !== "select") return null;
    const hp = (cx, cy, handle, key) => (
      <circle key={key} data-ui="1" cx={cx} cy={cy} r={handleR} fill="#FFFFFF" stroke={STEEL} strokeWidth={2.5 * u} style={{ cursor: "grab" }} onPointerDown={(e) => startDrag(e, el, handle)} />
    );
    if (el.type === "arrow") return <g data-ui="1">{hp(el.x1, el.y1, "p1", "p1")}{hp(el.x2, el.y2, "p2", "p2")}</g>;
    if (el.type === "ellipse" || el.type === "cloud") {
      const { xa, ya, xb, yb } = normRect(el);
      return (
        <g data-ui="1">
          <rect x={xa} y={ya} width={xb - xa} height={yb - ya} fill="none" stroke={STEEL} strokeWidth={1.5 * u} strokeDasharray={`${6 * u} ${5 * u}`} />
          {hp(xb, yb, "size", "size")}
        </g>
      );
    }
    return null;
  };

  const renderCallout = (el) => {
    const lay = calloutLayout(el, u, W);
    if (!lay || el.lx == null) return null;
    const { xa, ya, xb, yb } = normRect(el);
    const lcx = el.lx + lay.w / 2;
    const lcy = el.ly + lay.h / 2;
    const ccx = (xa + xb) / 2;
    const ccy = (ya + yb) / 2;
    const dx = ccx - lcx;
    const dy = ccy - lcy;
    const [sx, sy] = rayRectExit(lcx, lcy, dx, dy, { xa: el.lx, ya: el.ly, xb: el.lx + lay.w, yb: el.ly + lay.h });
    const [ex, ey] = rayRectExit(ccx, ccy, -dx, -dy, { xa, ya, xb, yb });
    const inside = sx >= xa && sx <= xb && sy >= ya && sy <= yb;
    const head = 3.2 * el.width;
    const len = dist(sx, sy, ex, ey);
    const shorten = len > head ? (len - head * 0.7) / len : 0;
    const mx = sx + (ex - sx) * shorten;
    const my = sy + (ey - sy) * shorten;
    const isSel = el.id === selectedId && tool === "select";
    return (
      <g>
        {!inside && len > head * 1.5 && (
          <>
            <line x1={sx} y1={sy} x2={mx} y2={my} stroke={el.color} strokeWidth={el.width * 0.75} strokeLinecap="round" />
            <polygon points={arrowHeadPts(sx, sy, ex, ey, head)} fill={el.color} />
          </>
        )}
        <rect x={el.lx} y={el.ly} width={lay.w} height={lay.h} rx={lay.fs * 0.35} fill="#FFFFFF" fillOpacity={0.94} stroke={el.color} strokeWidth={el.width * 0.6} style={{ cursor: tool === "select" ? "move" : undefined }} onPointerDown={(e) => startDrag(e, el, "label")} />
        <text x={el.lx + lay.pad} y={el.ly + lay.pad + lay.fs * 0.92} fontFamily={FONT} fontSize={lay.fs} fontWeight={700} fill={INK} style={{ pointerEvents: "none" }}>
          {lay.lines.map((ln, i) => <tspan key={i} x={el.lx + lay.pad} dy={i === 0 ? 0 : lay.lineH}>{ln}</tspan>)}
        </text>
        {isSel && <rect data-ui="1" x={el.lx - 3 * u} y={el.ly - 3 * u} width={lay.w + 6 * u} height={lay.h + 6 * u} rx={lay.fs * 0.4} fill="none" stroke={STEEL} strokeWidth={1.5 * u} strokeDasharray={`${6 * u} ${5 * u}`} />}
      </g>
    );
  };

  const renderElement = (el, isDraft = false) => {
    const sel = !isDraft && tool === "select";
    const common = { stroke: el.color, fill: "none", strokeWidth: el.width, strokeLinecap: "round", strokeLinejoin: "round", style: { cursor: sel ? "move" : undefined }, onPointerDown: isDraft ? undefined : (e) => startDrag(e, el) };
    const hit = { stroke: "transparent", fill: "none", strokeWidth: Math.max(el.width * 3, 24 * u), strokeLinecap: "round", style: common.style, onPointerDown: common.onPointerDown };
    switch (el.type) {
      case "pen":
      case "highlight": {
        const d = smoothPath(el.points);
        return (
          <g key={el.id}>
            <path d={d} {...common} opacity={el.type === "highlight" ? 0.45 : 1} />
            {!isDraft && <path d={d} {...hit} />}
          </g>
        );
      }
      case "ellipse": {
        const { xa, ya, xb, yb } = normRect(el);
        return (
          <g key={el.id}>
            <ellipse cx={(xa + xb) / 2} cy={(ya + yb) / 2} rx={Math.max(1, (xb - xa) / 2)} ry={Math.max(1, (yb - ya) / 2)} {...common} />
            {!isDraft && <ellipse cx={(xa + xb) / 2} cy={(ya + yb) / 2} rx={Math.max(1, (xb - xa) / 2)} ry={Math.max(1, (yb - ya) / 2)} {...hit} />}
            {renderHandles(el)}
          </g>
        );
      }
      case "arrow": {
        const head = 3.4 * el.width;
        const len = dist(el.x1, el.y1, el.x2, el.y2);
        const k = len > head ? (len - head * 0.7) / len : 0;
        const mx = el.x1 + (el.x2 - el.x1) * k;
        const my = el.y1 + (el.y2 - el.y1) * k;
        return (
          <g key={el.id}>
            <line x1={el.x1} y1={el.y1} x2={mx} y2={my} {...common} />
            <polygon points={arrowHeadPts(el.x1, el.y1, el.x2, el.y2, head)} fill={el.color} onPointerDown={common.onPointerDown} style={common.style} />
            {!isDraft && <line x1={el.x1} y1={el.y1} x2={el.x2} y2={el.y2} {...hit} />}
            {renderHandles(el)}
          </g>
        );
      }
      case "cloud": {
        const { xa, ya, xb, yb } = normRect(el);
        return (
          <g key={el.id}>
            <path d={cloudPath(el, 22 * u * (el.width / (5 * u)))} {...common} />
            {!isDraft && <rect x={xa} y={ya} width={Math.max(1, xb - xa)} height={Math.max(1, yb - ya)} fill="transparent" style={common.style} onPointerDown={common.onPointerDown} />}
            {!isDraft && renderCallout(el)}
            {renderHandles(el)}
          </g>
        );
      }
      case "text": {
        const fs = (el.fontScale || 1) * 30 * u;
        const lines = String(el.text || "").split("\n");
        const w = Math.max(...lines.map((l) => measure(l, fs, 700)));
        const isSel = el.id === selectedId && tool === "select";
        return (
          <g key={el.id}>
            <text x={el.x} y={el.y} fontFamily={FONT} fontSize={fs} fontWeight={700} fill={el.color} stroke={haloFor(el.color)} strokeWidth={fs * 0.14} strokeLinejoin="round" paintOrder="stroke" style={common.style} onPointerDown={common.onPointerDown}>
              {lines.map((ln, i) => <tspan key={i} x={el.x} dy={i === 0 ? 0 : fs * 1.2}>{ln}</tspan>)}
            </text>
            {!isDraft && <rect x={el.x - fs * 0.2} y={el.y - fs} width={w + fs * 0.4} height={fs * 1.2 * lines.length + fs * 0.1} fill="transparent" style={common.style} onPointerDown={common.onPointerDown} />}
            {isSel && <rect data-ui="1" x={el.x - fs * 0.25} y={el.y - fs * 1.05} width={w + fs * 0.5} height={fs * 1.2 * lines.length + fs * 0.2} fill="none" stroke={STEEL} strokeWidth={1.5 * u} strokeDasharray={`${6 * u} ${5 * u}`} />}
          </g>
        );
      }
      default:
        return null;
    }
  };

  const renderStamp = () => {
    const L = stampLayout;
    if (!L) return null;
    let cursorY = L.y + L.pad;
    const textX = L.x + L.pad + L.accentW + L.labelW;
    return (
      <g data-testid="markup-stamp">
        <rect x={L.x} y={L.y} width={L.w} height={L.h} rx={L.fs * 0.45} fill={INK} fillOpacity={0.86} />
        <rect x={L.x} y={L.y} width={L.accentW} height={L.h} rx={L.accentW / 2} fill={STEEL} />
        {L.laid.map((row, i) => {
          const top = cursorY;
          cursorY += row.lines.length * L.lineH + L.rowGap;
          return (
            <g key={row.label}>
              <text x={L.x + L.pad + L.accentW} y={top + L.fs * 0.95} fontFamily={FONT} fontSize={L.kickerPx} fontWeight={700} fill={PALE} letterSpacing={L.kickerPx * 0.12}>{row.label}</text>
              <text x={textX} y={top + L.fs * 0.95} fontFamily={FONT} fontSize={L.fs} fontWeight={row.weight} fill="#FFFFFF">
                {row.lines.map((ln, j) => <tspan key={j} x={textX} dy={j === 0 ? 0 : L.lineH}>{ln}</tspan>)}
              </text>
              {i < L.laid.length - 1 && <line x1={textX} y1={cursorY - L.rowGap / 2} x2={L.x + L.w - L.pad} y2={cursorY - L.rowGap / 2} stroke={PALE} strokeOpacity={0.22} strokeWidth={Math.max(1, u)} />}
            </g>
          );
        })}
      </g>
    );
  };

  const canEditText = selected && (selected.type === "text" || selected.type === "cloud");
  const stampFilled = STAMP_ROWS.some(([, k]) => String(stamp[k] || "").trim());

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent className="flex h-[100dvh] w-screen max-w-none flex-col gap-0 overflow-hidden rounded-none p-0 sm:h-[92vh] sm:w-[96vw] sm:max-w-6xl sm:rounded-xl [&>button]:hidden" data-testid="photo-markup-dialog">
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">Draw on the photo, add callouts and an info stamp, then apply the markup.</DialogDescription>

        {/* header */}
        <div className="flex items-center gap-2 border-b border-border px-3 py-2">
          <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => onOpenChange(false)} aria-label="Close markup" disabled={saving} data-testid="markup-cancel"><X className="h-5 w-5" /></Button>
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-1.5 whitespace-nowrap text-[10px] font-semibold uppercase tracking-wide text-primary"><PenLine className="h-3 w-3" /> Photo markup</p>
            <p className="truncate font-heading text-sm font-semibold" data-testid="markup-title">{title}{subtitle ? <span className="font-normal text-muted-foreground"> · {subtitle}</span> : null}</p>
          </div>
          <Popover open={stampOpen} onOpenChange={setStampOpen}>
            <PopoverTrigger asChild>
              <Button variant={stamp.enabled ? "default" : "outline"} size="sm" className="h-10 shrink-0 px-2.5 sm:px-3" aria-label={stamp.enabled ? "Info stamp on — edit" : "Add info stamp"} data-testid="markup-stamp-toggle">
                <BadgeInfo className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Info stamp{stamp.enabled ? " · on" : ""}</span>{stamp.enabled && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-primary-foreground sm:hidden" />}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[min(92vw,380px)] space-y-3" data-testid="markup-stamp-panel">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">Info stamp</p>
                  <p className="text-[11px] text-muted-foreground">Neat block on the photo — text wraps and shrinks to fit.</p>
                </div>
                <Switch checked={stamp.enabled} onCheckedChange={(v) => setStamp((s) => ({ ...s, enabled: v }))} aria-label="Show info stamp" data-testid="markup-stamp-switch" />
              </div>
              <div className="grid grid-cols-2 gap-1.5" data-testid="markup-stamp-position">
                {["bottom", "top"].map((p) => (
                  <Button key={p} type="button" variant="outline" size="sm" className={`h-9 capitalize ${stamp.position === p ? "border-primary bg-accent font-medium" : ""}`} onClick={() => setStamp((s) => ({ ...s, position: p }))} data-testid={`markup-stamp-pos-${p}`}>{p} of photo</Button>
                ))}
              </div>
              <div className="space-y-2">
                <div className="space-y-1">
                  <Label htmlFor="mk-site" className="text-[11px] uppercase tracking-wide text-muted-foreground">Site / address</Label>
                  <Input id="mk-site" value={stamp.site} onChange={(e) => setStampField("site", e.target.value)} maxLength={140} className="h-9" data-testid="markup-stamp-site" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label htmlFor="mk-unit" className="text-[11px] uppercase tracking-wide text-muted-foreground">Unit (if applicable)</Label>
                    <Input id="mk-unit" value={stamp.unit} onChange={(e) => setStampField("unit", e.target.value)} placeholder="e.g. Unit 3" maxLength={60} className="h-9" data-testid="markup-stamp-unit" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="mk-area" className="text-[11px] uppercase tracking-wide text-muted-foreground">Area / room</Label>
                    <Input id="mk-area" value={stamp.area} onChange={(e) => setStampField("area", e.target.value)} placeholder="e.g. Kitchen" maxLength={80} className="h-9" data-testid="markup-stamp-area" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="mk-desc" className="text-[11px] uppercase tracking-wide text-muted-foreground">Defect description</Label>
                  <Textarea id="mk-desc" value={stamp.description} onChange={(e) => setStampField("description", e.target.value)} rows={3} maxLength={400} className="text-sm" data-testid="markup-stamp-description" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="mk-trade" className="text-[11px] uppercase tracking-wide text-muted-foreground">Trade / supplier to action</Label>
                  <Input id="mk-trade" value={stamp.trade} onChange={(e) => setStampField("trade", e.target.value)} placeholder="e.g. Painter — ABC Painting · book in" maxLength={120} className="h-9" data-testid="markup-stamp-trade" />
                </div>
              </div>
              {stamp.enabled && !stampFilled && <p className="text-[11px] text-destructive" data-testid="markup-stamp-empty-hint">Fill in at least one field for the stamp to appear.</p>}
            </PopoverContent>
          </Popover>
          <Button size="sm" className="h-10" onClick={save} disabled={saving || !dims} data-testid="markup-save">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Check className="mr-1.5 h-4 w-4" />} {saving ? "Applying…" : "Apply"}
          </Button>
        </div>

        {/* tools */}
        <div className="border-b border-border bg-card/80 px-2 py-1.5 backdrop-blur" data-testid="markup-toolbar">
          <div className="flex items-center gap-1 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {TOOLS.map((t) => (
              <Button key={t.id} type="button" variant={tool === t.id ? "default" : "ghost"} size="icon" className="h-11 w-11 shrink-0" onClick={() => { setTool(t.id); if (t.id !== "select") setSelectedId(null); }} aria-label={t.label} aria-pressed={tool === t.id} title={t.label} data-testid={`markup-tool-${t.id}`}>
                <t.icon className="h-5 w-5" />
              </Button>
            ))}
            <span className="mx-1 h-7 w-px shrink-0 bg-border" />
            {SIZES.map((s) => (
              <Button key={s.id} type="button" variant={size === s.id ? "secondary" : "ghost"} size="icon" className="h-11 w-11 shrink-0" onClick={() => pickSize(s.id)} aria-label={`${s.label} stroke`} aria-pressed={size === s.id} title={s.label} data-testid={`markup-size-${s.id}`}>
                <span className="rounded-full bg-current" style={{ width: 6 + s.k * 6, height: 6 + s.k * 6 }} />
              </Button>
            ))}
            <span className="mx-1 h-7 w-px shrink-0 bg-border" />
            <Button type="button" variant="ghost" size="icon" className="h-11 w-11 shrink-0" onClick={undo} disabled={!history.length} aria-label="Undo" data-testid="markup-undo"><Undo2 className="h-5 w-5" /></Button>
            <Button type="button" variant="ghost" size="icon" className="h-11 w-11 shrink-0" onClick={redo} disabled={!redoStack.length} aria-label="Redo" data-testid="markup-redo"><Redo2 className="h-5 w-5" /></Button>
            {canEditText && <Button type="button" variant="outline" size="sm" className="h-11 shrink-0" onClick={editSelectedText} data-testid="markup-edit-text"><Type className="mr-1 h-4 w-4" /> Edit text</Button>}
            <Button type="button" variant="ghost" size="icon" className="h-11 w-11 shrink-0 text-destructive hover:text-destructive" onClick={deleteSelected} disabled={!selected} aria-label="Delete selected" data-testid="markup-delete"><Trash2 className="h-5 w-5" /></Button>
            <Button type="button" variant="ghost" size="sm" className="h-11 shrink-0 text-xs text-muted-foreground" onClick={() => { if (elements.length) commit([]); setSelectedId(null); }} disabled={!elements.length} data-testid="markup-clear">Clear all</Button>
          </div>
          {/* colours */}
          <div className="mt-1.5 flex items-center gap-1.5 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="markup-colours">
            {tool === "highlight" ? (
              <>
                <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Highlight</span>
                {MARKUP_HIGHLIGHTS.map((c) => (
                  <button key={c.id} type="button" onClick={() => pickColor(c.hex, true)} className={`h-9 w-9 shrink-0 rounded-full border-2 transition-transform duration-150 hover:scale-105 ${hiColor === c.hex ? "border-foreground scale-105" : "border-border"}`} style={{ backgroundColor: c.hex, opacity: 0.85 }} aria-label={`${c.id} highlighter`} aria-pressed={hiColor === c.hex} data-testid={`markup-highlight-${c.id}`} />
                ))}
              </>
            ) : (
              <>
                <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Colour</span>
                {MARKUP_SOLIDS.map((c) => (
                  <button key={c.id} type="button" onClick={() => pickColor(c.hex)} className={`h-9 w-9 shrink-0 rounded-full border-2 transition-transform duration-150 hover:scale-105 ${color === c.hex ? "border-foreground scale-105" : "border-border"}`} style={{ backgroundColor: c.hex }} aria-label={`${c.id} colour`} aria-pressed={color === c.hex} data-testid={`markup-color-${c.id}`} />
                ))}
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button" className={`flex h-9 shrink-0 items-center gap-1.5 rounded-full border-2 px-2.5 text-xs transition-colors duration-150 hover:bg-muted/50 ${!MARKUP_SOLIDS.some((c) => c.hex === color) ? "border-foreground" : "border-border"}`} aria-label="Spectrum colour" data-testid="markup-spectrum">
                      <span className="h-5 w-5 rounded-full border border-border" style={{ background: "conic-gradient(red, yellow, lime, cyan, blue, magenta, red)" }} /> Spectrum
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-72 space-y-3" data-testid="markup-spectrum-panel">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold">Pick any hue</p>
                      <span className="h-6 w-6 rounded-full border border-border" style={{ backgroundColor: hslToHex(hue, 0.9, 0.5) }} data-testid="markup-spectrum-preview" />
                    </div>
                    <div className="h-3 rounded-full" style={{ background: "linear-gradient(to right, hsl(0 90% 50%), hsl(60 90% 50%), hsl(120 90% 50%), hsl(180 90% 50%), hsl(240 90% 50%), hsl(300 90% 50%), hsl(360 90% 50%))" }} />
                    <Slider value={[hue]} min={0} max={360} step={1} onValueChange={(v) => { setHue(v[0]); pickColor(hslToHex(v[0], 0.9, 0.5)); }} aria-label="Hue" data-testid="markup-spectrum-hue" />
                  </PopoverContent>
                </Popover>
              </>
            )}
          </div>
        </div>

        {/* canvas */}
        <div
          ref={boxRef}
          className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-[hsl(var(--builtos-navy))]"
          style={{ touchAction: "none", cursor: gestureRef.current?.type === "pan" ? "grabbing" : undefined }}
          onPointerDownCapture={onBoxPointerDownCapture}
          onPointerDown={onBoxPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          data-testid="markup-canvas-box"
        >
          {loadError ? (
            <p data-markup-ui="1" className="rounded-lg bg-background/90 px-4 py-3 text-sm text-destructive" data-testid="markup-load-error">{loadError}</p>
          ) : !dims || !fit ? (
            <Loader2 className="h-6 w-6 animate-spin text-primary-foreground/70" data-testid="markup-loading" />
          ) : (
            <div
              className="relative select-none"
              style={{ width: dispW, height: dispH, transform: `translate(${view.x}px, ${view.y}px) scale(${view.z})`, transformOrigin: "center center", willChange: "transform" }}
              data-testid="markup-viewport"
              data-zoom={view.z.toFixed(2)}
            >
              <img src={imgUrl} alt="Photo being marked up" className="absolute inset-0 h-full w-full" draggable={false} style={{ pointerEvents: "none" }} data-testid="markup-photo" />
              <svg
                ref={svgRef}
                viewBox={`0 0 ${W} ${H}`}
                width={dispW}
                height={dispH}
                className="absolute inset-0"
                style={{ touchAction: "none", cursor: tool === "select" ? (view.z > 1 ? "grab" : "default") : tool === "text" ? "text" : "crosshair" }}
                onPointerDown={onPointerDown}
                data-testid="markup-canvas"
              >
                {elements.map((el) => renderElement(el))}
                {draft && renderElement(draft, true)}
                {renderStamp()}
              </svg>
            </div>
          )}

          {/* zoom controls */}
          {dims && fit ? (
            <div data-markup-ui="1" className="absolute right-2 top-2 flex flex-col items-center gap-0.5 rounded-xl border border-border bg-background/90 p-1 shadow-lg backdrop-blur" data-testid="markup-zoom-controls">
              <Button type="button" variant="ghost" size="icon" className="h-10 w-10" onClick={() => zoomBy(ZOOM_STEP)} disabled={view.z >= MAX_ZOOM - 0.001} aria-label="Zoom in" title="Zoom in (scroll or pinch)" data-testid="markup-zoom-in"><ZoomIn className="h-5 w-5" /></Button>
              <span className="w-full select-none text-center text-[11px] font-semibold tabular-nums text-muted-foreground" aria-live="polite" data-testid="markup-zoom-level">{Math.round(view.z * 100)}%</span>
              <Button type="button" variant="ghost" size="icon" className="h-10 w-10" onClick={() => zoomBy(1 / ZOOM_STEP)} disabled={view.z <= MIN_ZOOM + 0.001} aria-label="Zoom out" title="Zoom out" data-testid="markup-zoom-out"><ZoomOut className="h-5 w-5" /></Button>
              <Button type="button" variant="ghost" size="icon" className="h-10 w-10" onClick={resetView} disabled={view.z <= MIN_ZOOM + 0.001 && !view.x && !view.y} aria-label="Fit photo to screen" title="Fit to screen" data-testid="markup-zoom-reset"><Maximize className="h-5 w-5" /></Button>
            </div>
          ) : null}

          {/* text entry bar */}
          {textEdit && (
            <div data-markup-ui="1" className="absolute inset-x-2 bottom-2 flex items-center gap-2 rounded-xl border border-border bg-background/95 p-2 shadow-lg backdrop-blur" data-testid="markup-text-bar">
              <Input
                autoFocus
                value={textEdit.value}
                onChange={(e) => setTextEdit((t) => ({ ...t, value: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitText(); } if (e.key === "Escape") setTextEdit(null); }}
                placeholder={textEdit.mode === "cloud" ? "Callout text (optional) — e.g. Chip in benchtop, replace" : "Label text"}
                maxLength={160}
                className="h-11 text-base"
                data-testid="markup-text-input"
              />
              <Button size="icon" className="h-11 w-11 shrink-0" onClick={commitText} aria-label="Done" data-testid="markup-text-ok"><Check className="h-5 w-5" /></Button>
              <Button size="icon" variant="ghost" className="h-11 w-11 shrink-0" onClick={() => setTextEdit(null)} aria-label="Cancel text" data-testid="markup-text-cancel"><X className="h-5 w-5" /></Button>
            </div>
          )}
        </div>

        {/* footer hint */}
        <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
          <span className="truncate" data-testid="markup-hint">
            {tool === "select" ? `Tap a mark to move it · drag handles to resize${view.z > 1 ? " · drag empty space to pan" : " · swatch recolours it"}` : tool === "cloud" ? "Drag a box around the defect — then type the callout" : tool === "text" ? "Tap where the label should go" : tool === "highlight" ? "Drag to highlight" : "Drag on the photo"}
            <span className="hidden sm:inline"> · scroll or pinch to zoom</span>
          </span>
          <span className="flex shrink-0 items-center gap-1 whitespace-nowrap tabular-nums" data-testid="markup-count"><Palette className="h-3 w-3" /> {elements.length} mark{elements.length === 1 ? "" : "s"}{stampLayout ? " · stamp" : ""}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** Convenience: compute the saved-photo bytes from a data URL (for tile labels). */
export const markupDataUrlBytes = approxBytes;
